import { computed, ref, watch, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useUserDataStore } from '~/stores/userData';
import { useSmarticoUserInfos } from '~/stores/smartico/userInfos';
import { useSmarticoInit } from '~/stores/smartico/init';

/**
 * Composable para centralizar a lógica de verificação de usuário e Smartico prontos.
 * Usado nos componentes de gamificação (Missions, MiniGames, Tournaments, Store, etc.)
 *
 * Segue princípios SOLID:
 * - SRP: Responsabilidade única de verificar disponibilidade da API
 * - OCP: Aberto para extensão (apiMethods param), fechado para modificação
 * - LSP: Retorna interface consistente independente do tipo de usuário
 * - ISP: Interface mínima necessária
 * - DIP: Depende de abstrações (window._smartico), não de implementações específicas
 *
 * @param loadDataFn - Função opcional para carregar dados quando tudo estiver pronto
 * @param apiMethods - Métodos opcionais para verificar (default: verifica apenas api base)
 * @returns isUserReady, isSmarticoReady, isReady, initialLoadComplete e waitForReady
 */
export function useSmarticoUserReady(
  loadDataFn?: () => Promise<void>,
  apiMethods?: string[],
) {
  const { user, isLoading: userDataLoading } = storeToRefs(useUserDataStore());
  const { userInfosProfile } = storeToRefs(useSmarticoUserInfos());

  // Flag reativa para verificar se a API está realmente funcional
  const isSmarticoApiFunctional = ref(false);
  let checkInterval: ReturnType<typeof setInterval> | null = null;

  /**
   * Verifica se a API do Smartico está realmente funcional
   * Para usuário logado: verifica window._smartico.api
   * Para visitante: verifica window._smartico.vapi('EN')
   * Se apiMethods for passado, verifica se esses métodos existem
   */
  const checkSmarticoApi = () => {
    if (typeof window === 'undefined' || !window._smartico) {
      return false;
    }

    // Guard: só prossegue após _smartico.init() ter sido chamado.
    // Chamar vapi() antes disso dispara warnings do SDK (script carregado mas não inicializado).
    const { isReadySmartico } = storeToRefs(useSmarticoInit());

    if (!isReadySmartico.value) {
      return false;
    }

    const isLoggedIn = user.value && user.value.id;

    try {
      if (isLoggedIn) {
        // Para usuário logado, verifica se api está disponível
        if (!window._smartico.api) {
          return false;
        }

        // Se métodos específicos foram passados, verifica se existem
        if (apiMethods && apiMethods.length > 0) {
          return apiMethods.every(
            (method) =>
              typeof (window._smartico.api as any)[method] === 'function',
          );
        }

        // Verificação padrão: api existe e é um objeto
        return typeof window._smartico.api === 'object';
      } else {
        // Para visitante, verifica se vapi está disponível e funcional
        if (typeof window._smartico.vapi !== 'function') {
          return false;
        }

        const vapi = window._smartico.vapi('EN');

        if (!vapi) {
          return false;
        }

        // Se métodos específicos foram passados, verifica se existem
        if (apiMethods && apiMethods.length > 0) {
          return apiMethods.every(
            (method) => typeof (vapi as any)[method] === 'function',
          );
        }

        // Verificação padrão: vapi existe e é um objeto
        return typeof vapi === 'object';
      }
    } catch {
      // API ainda não está pronta
      return false;
    }
  };

  // Função para atualizar o estado da API
  const updateApiStatus = () => {
    const isReady = checkSmarticoApi();

    if (isReady && !isSmarticoApiFunctional.value) {
      isSmarticoApiFunctional.value = true;
      if (checkInterval) {
        clearInterval(checkInterval);
        checkInterval = null;
      }
    }
  };

  // Iniciar verificação no client
  if (import.meta.client) {
    updateApiStatus();
    if (!isSmarticoApiFunctional.value) {
      checkInterval = setInterval(updateApiStatus, 300);
    }
  }

  // Computed que verifica se o Smartico está pronto (usando verificação real)
  const isSmarticoApiReady = computed(() => {
    return isSmarticoApiFunctional.value;
  });

  // Computed que verifica se o usuário está pronto para carregar dados do Smartico
  const isUserReady = computed(() => {
    return (
      (!user.value || !user.value.id || userInfosProfile.value) &&
      !userDataLoading.value
    );
  });

  // Computed combinado: usuário E Smartico prontos
  const isReady = computed(() => {
    return isUserReady.value && isSmarticoApiReady.value;
  });

  // Flag que indica se o carregamento inicial foi concluído (evita piscadas no loading)
  const initialLoadComplete = ref<boolean>(false);

  // Watch que executa a função de carregamento quando tudo estiver pronto
  const waitForReady = (onReady?: () => Promise<void> | void) => {
    watch(
      isReady,
      async (ready) => {
        if (ready) {
          if (onReady) {
            await onReady();
          } else if (loadDataFn) {
            await loadDataFn();
          }
          initialLoadComplete.value = true;
        }
      },
      { immediate: true },
    );
  };

  // Cleanup do interval quando componente for desmontado
  onUnmounted(() => {
    if (checkInterval) {
      clearInterval(checkInterval);
      checkInterval = null;
    }
  });

  return {
    isUserReady,
    isSmarticoApiReady,
    isReady,
    initialLoadComplete,
    waitForReady,
    user,
    userDataLoading,
    userInfosProfile,
  };
}
